import { useState, useEffect } from 'react';
import { Search, User, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import { supabase } from '../../lib/supabase';
import {
  isValidPhone,
  isValidEmail,
  isValidCpf,
  isValidCnpj
} from '../../utils/validators';
import { useLocalization } from '../../hooks/useLocalization';
import { maskCpfCnpj, maskInternationalPhone, maskPhone, maskRegistration } from '../../utils/masks';

export default function ClientSearchWithRegistration({ officeId, onClientSelected, selectedClient }) {
  const { settings } = useLocalization();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const [newClient, setNewClient] = useState({
    nome: '',
    telefone: '',
    cpf: '',
    email: ''
  });

  // Load all clients for search
  useEffect(() => {
    if (!officeId) return;

    const loadClients = async () => {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, nome, telefone, email, cpf, documento')
        .eq('oficina_id', officeId)
        .is('removed_at', null)
        .order('nome');

      if (!error) {
        setClients(data || []);
      }
    };

    loadClients();
  }, [officeId]);

  // Search clients
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const term = searchTerm.trim().toLowerCase();
    const numericTerm = searchTerm.replace(/\D/g, '');
    const filtered = (clients || []).filter(client =>
      client.nome?.toLowerCase().includes(term) ||
      (numericTerm.length >= 2 && client.telefone?.replace(/\D/g, '').includes(numericTerm)) ||
      (numericTerm.length >= 3 && (client.documento || client.cpf)?.replace(/\D/g, '').includes(numericTerm)) ||
      client.email?.toLowerCase().includes(term)
    );

    setSearchResults(filtered.slice(0, 5)); // Limit to 5 results
  }, [searchTerm, clients]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (selectedClient && value !== selectedClient.nome) {
      onClientSelected(null);
    }
  };

  const handleClientSelect = (client) => {
    onClientSelected(client);
    setSearchTerm(client.nome);
    setSearchResults([]);
    setShowRegistrationForm(false);
  };

  const handleCreateClient = async () => {
    if (!newClient.nome.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }

    if (!newClient.telefone.trim()) {
      toast.error('Telefone é obrigatório');
      return;
    }

    if (!isValidPhone(newClient.telefone, settings.countryCode)) {
      toast.error('Informe um telefone válido.');
      return;
    }

    if (newClient.email.trim() && !isValidEmail(newClient.email)) {
      toast.error('Informe um e-mail válido.');
      return;
    }

    if (settings.countryCode === 'BR' && newClient.cpf.trim()) {
      const documentoValido =
        isValidCpf(newClient.cpf) || isValidCnpj(newClient.cpf);

      if (!documentoValido) {
        toast.error('Informe um CPF ou CNPJ válido.');
        return;
      }
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clientes')
        .insert({
          oficina_id: officeId,
          nome: newClient.nome.trim(),
          telefone: newClient.telefone.trim(),
          cpf: settings.countryCode === 'BR' ? newClient.cpf.trim() || null : null,
          documento: newClient.cpf.trim() || null,
          tipo_documento: settings.registrationLabel,
          pais_codigo: settings.countryCode,
          email: newClient.email.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local clients list
      setClients(prev => [...prev, data]);

      // Auto-select the new client
      handleClientSelect(data);

      // Reset form
      setNewClient({ nome: '', telefone: '', cpf: '', email: '' });
      setShowRegistrationForm(false);

      toast.success('Cliente cadastrado com sucesso!');
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(
        'Não foi possível cadastrar o cliente. Verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={18} />
          <Input
            placeholder={`Buscar por nome, telefone, ${settings.registrationLabel} ou email`}
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto">
            <div className="p-2 space-y-1">
              {searchResults.map(client => (
                <button
                  key={client.id}
                  onClick={() => handleClientSelect(client)}
                  className="w-full text-left p-3 rounded-lg hover:bg-surface2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <User size={16} className="text-zinc-400" />
                    <div>
                      <div className="font-medium">{client.nome}</div>
                      <div className="text-sm text-zinc-400">{client.telefone}</div>
                    </div>
                  </div>
                </button>
              ))}
              {!selectedClient && (
                <button
                  type="button"
                  onClick={() => {
                    setNewClient((prev) => ({ ...prev, nome: searchTerm }));
                    setShowRegistrationForm(true);
                    setSearchResults([]);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border p-3 text-left text-sm text-orange-300 hover:bg-orange-500/10"
                >
                  <Plus size={16} />
                  Cadastrar novo cliente
                </button>
              )}
            </div>
          </Card>
        )}
      </div>

      {!selectedClient && !showRegistrationForm && !searchTerm && (
        <Button
          type="button"
          onClick={() => setShowRegistrationForm(true)}
          variant="ghost"
          className="w-full"
        >
          <Plus size={16} className="mr-2" />
          Cadastrar novo cliente
        </Button>
      )}

      {/* Selected Client Display */}
      {selectedClient && !showRegistrationForm && (
        <Card className="bg-accent/5 border-accent/20">
          <div className="flex items-center gap-3">
            <User className="text-accent" size={20} />
            <div>
              <div className="font-medium">{selectedClient.nome}</div>
              <div className="text-sm text-zinc-400">{selectedClient.telefone}</div>
            </div>
          </div>
        </Card>
      )}

      {/* No Results - Show Add Client Button */}
      {searchTerm && searchResults.length === 0 && !showRegistrationForm && !selectedClient && (
        <Card className="border-dashed border-border">
          <div className="text-center py-4">
            <User size={32} className="mx-auto mb-2 text-zinc-400" />
            <p className="text-zinc-300 mb-3">Nenhum cliente encontrado</p>
            <Button
              onClick={() => setShowRegistrationForm(true)}
              variant="ghost"
              className="w-full"
            >
              <Plus size={16} className="mr-2" />
              Cadastrar novo cliente
            </Button>
          </div>
        </Card>
      )}

      {/* Registration Form */}
      {showRegistrationForm && (
        <Card className="border-accent/20">
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="text-accent" size={20} />
              <h3 className="font-semibold">Cadastrar Novo Cliente</h3>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Nome completo *</label>
              <Input
                placeholder="Ex: Maria Silva"
                value={newClient.nome}
                onChange={(e) => setNewClient(prev => ({ ...prev, nome: e.target.value }))}
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Telefone *</label>
              <Input
                placeholder={settings.countryCode === 'BR' ? '(00) 00000-0000' : '+1 555 123 4567'}
                mask={settings.countryCode === 'BR' ? 'phone' : 'internationalPhone'}
                value={newClient.telefone}
                onChange={(e) => setNewClient(prev => ({ ...prev, telefone: settings.countryCode === 'BR' ? maskPhone(e.target.value) : maskInternationalPhone(e.target.value) }))}
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">{settings.registrationLabel}</label>
              <Input
                placeholder={settings.registrationLabel}
                mask={settings.countryCode === 'BR' ? 'cpfCnpj' : 'registration'}
                value={newClient.cpf}
                onChange={(e) => setNewClient(prev => ({ ...prev, cpf: settings.countryCode === 'BR' ? maskCpfCnpj(e.target.value) : maskRegistration(e.target.value) }))}
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <Input
                type="email"
                placeholder="cliente@email.com"
                value={newClient.email}
                onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                disabled={loading}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRegistrationForm(false);
                  setNewClient({ nome: '', telefone: '', cpf: '', email: '' });
                }}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateClient}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Cadastrando...' : 'Cadastrar Cliente'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
